import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  MoreHorizontal,
  Play,
  Square,
  Loader2,
  Trash2,
  Volume2,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { getEffectiveSceneValues } from '@/@types/reelScenes';
import {
  WORDS_PER_SECOND,
  DURATION_LIMIT_REGULAR,
  DURATION_LIMIT_AVATAR,
} from '@/constants/voiceover';
import { DeliveryStyleBadge } from './DeliveryStyleBadge';
import type { ISceneTextItemProps } from '@/@types/sceneEditorV2';

function SceneTextItem({
  scene,
  index,
  isActive,
  onSelect,
  onPlayAudio,
  onDeleteScene,
  onRegenerateAudio,
  onUpdateVoiceoverText,
  onUpdateDeliveryStyle,
  isAudioGenerating,
  isAudioPlaying,
  hasAudio,
  generatedVoiceoverText,
  actualDurationSeconds,
}: ISceneTextItemProps) {
  const effective = getEffectiveSceneValues(scene);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(effective.voiceoverText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const showDeliveryStyle =
    effective.sceneType === 'avatar_only' || effective.sceneType === 'image_avatar';

  const isAvatarScene = effective.sceneType === 'avatar_only';
  const maxDurationSeconds = isAvatarScene ? DURATION_LIMIT_AVATAR : DURATION_LIMIT_REGULAR;

  // Stale audio detection: compare current text with what was used for TTS
  const isAudioStale = useMemo(() => {
    if (!generatedVoiceoverText || !hasAudio) return false;
    return effective.voiceoverText.trim() !== generatedVoiceoverText.trim();
  }, [effective.voiceoverText, generatedVoiceoverText, hasAudio]);

  // Duration: use actual TTS duration when available (view mode only), fall back to word-count estimate
  const durationInfo = useMemo(() => {
    const text = isEditing ? editText : effective.voiceoverText;
    const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
    const estimatedSeconds = wordCount / WORDS_PER_SECOND;

    // Use actual duration in view mode when we have fresh audio
    const useActual = !isEditing && actualDurationSeconds !== null;
    const displaySeconds = useActual ? actualDurationSeconds : estimatedSeconds;
    const isOverLimit = displaySeconds > maxDurationSeconds;
    const isEstimated = !useActual;

    return { wordCount, displaySeconds, isOverLimit, isEstimated };
  }, [isEditing, editText, effective.voiceoverText, maxDurationSeconds, actualDurationSeconds]);

  // Sync edit text when scene voiceover changes externally (only when not actively editing).
  // Guard by isEditing to avoid clobbering in-progress user input.
  if (!isEditing && editText !== effective.voiceoverText) {
    setEditText(effective.voiceoverText);
  }

  // Auto-focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [isEditing]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    const trimmed = editText.trim();
    if (trimmed && trimmed !== effective.voiceoverText) {
      onUpdateVoiceoverText(trimmed);
    }
  }, [editText, effective.voiceoverText, onUpdateVoiceoverText]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        setEditText(effective.voiceoverText);
        setIsEditing(false);
      } else {
        e.stopPropagation();
      }
    },
    [effective.voiceoverText]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        'group relative cursor-pointer px-3 py-2 transition-colors',
        isActive
          ? 'border-l-2 border-l-primary border-y border-y-border bg-primary/15'
          : 'border-l-2 border-l-transparent border-y border-y-transparent hover:bg-background-secondary/50'
      )}
      onClick={onSelect}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Header row: scene number + actions */}
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-medium text-foreground-muted">Scene {index + 1}</span>
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100">
          <Button
            variant="ghost"
            size="icon-sm"
            className="size-6"
            onClick={e => {
              e.stopPropagation();
              onPlayAudio();
            }}
            disabled={isAudioGenerating}
          >
            {isAudioGenerating ? (
              <Loader2 className="size-3 animate-spin" />
            ) : isAudioPlaying ? (
              <Square className="size-3" />
            ) : (
              <Play className="size-3" />
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="size-6"
                onClick={e => e.stopPropagation()}
              >
                <MoreHorizontal className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation();
                  onPlayAudio();
                }}
                disabled={!hasAudio}
              >
                <Volume2 className="size-4" />
                Preview Speech
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation();
                  onRegenerateAudio();
                }}
              >
                <RefreshCw className="size-4" />
                Regenerate Audio
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={e => {
                  e.stopPropagation();
                  onDeleteScene();
                }}
              >
                <Trash2 className="size-4" />
                Delete Scene
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Voiceover text (click to edit when scene is active) */}
      {isEditing ? (
        <>
          <Textarea
            ref={textareaRef}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="min-h-10 resize-none border-none bg-transparent p-0 text-base shadow-none focus-visible:ring-0"
            onClick={e => e.stopPropagation()}
          />
          {/* Duration stats while editing */}
          <div className="mt-1 space-y-0.5">
            <p
              className={cn(
                'text-xs',
                durationInfo.isOverLimit ? 'text-amber-500' : 'text-foreground-muted'
              )}
            >
              {durationInfo.wordCount} words · ~{durationInfo.displaySeconds.toFixed(1)}s estimated
            </p>
            {durationInfo.isOverLimit && (
              <p className="flex items-center gap-1 text-xs text-amber-500">
                <AlertTriangle className="size-3 shrink-0" />~
                {durationInfo.displaySeconds.toFixed(1)}s estimated (max {maxDurationSeconds}s)
              </p>
            )}
          </div>
        </>
      ) : (
        <p
          className={cn(
            'line-clamp-3 text-base leading-relaxed text-foreground',
            isActive && 'cursor-text hover:bg-primary/5 rounded px-1 -mx-1'
          )}
          onClick={e => {
            if (isActive) {
              e.stopPropagation();
              setIsEditing(true);
            }
          }}
          onKeyDown={e => {
            if (isActive && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              e.stopPropagation();
              setIsEditing(true);
            }
          }}
        >
          {effective.voiceoverText}
        </p>
      )}

      {/* Duration info (view mode, no warning) */}
      {!isEditing && !isAudioStale && !durationInfo.isOverLimit && (
        <p className="mt-1 text-xs text-foreground-muted">
          {durationInfo.wordCount} words ·{' '}
          {durationInfo.isEstimated
            ? `~${durationInfo.displaySeconds.toFixed(1)}s estimated`
            : `${durationInfo.displaySeconds.toFixed(1)}s`}
        </p>
      )}

      {/* Stale audio indicator (view mode) */}
      {!isEditing && isAudioStale && (
        <p className="mt-1 flex items-center gap-1 text-xs text-amber-500">
          <AlertTriangle className="size-3 shrink-0" />
          Audio needs regeneration
        </p>
      )}

      {/* Duration warning (view mode, non-editing) */}
      {!isEditing && !isAudioStale && durationInfo.isOverLimit && (
        <p className="mt-1 flex items-center gap-1 text-xs text-amber-500">
          <AlertTriangle className="size-3 shrink-0" />
          {durationInfo.isEstimated ? '~' : ''}
          {durationInfo.displaySeconds.toFixed(1)}s{durationInfo.isEstimated ? ' estimated' : ''}{' '}
          (max {maxDurationSeconds}s)
        </p>
      )}

      {/* Delivery style badge (avatar_only / image_avatar only) */}
      {showDeliveryStyle && <DeliveryStyleBadge onSave={onUpdateDeliveryStyle} />}
    </div>
  );
}

export { SceneTextItem };
